import AuthLayout from '../../components/layout/AuthLayout.jsx';
import BaseLink from '../../components/misc/BaseLink.jsx';
import { useLazyGetOrganizationsDashboardQuery } from '../../api/organizations.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetCountriesQuery } from '../../api/misc.js';
import BaseCard from '../../components/misc/BaseCard.jsx';
import DashboardCountryCarousel from '../../components/misc/DashboardCountryCarousel.jsx';
import DashboardMetricsCarousel from '../../components/misc/DashboardMetricsCarousel.jsx';
import circle from '../../resources/images/circle.png';
import BaseButton from '../../components/form/BaseButton.jsx';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { formatNumbers } from '../../utils/formatNumbers.js';
import { pdf } from '@react-pdf/renderer';
import dayjs from 'dayjs';

const OrganizationsDashboard = () => {
    const organizationSwitch = useSelector((state) => state.organizationSwitch);
    const { user } = useCurrentUser();
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState([]);
    const [dashboardResponse] = useLazyGetOrganizationsDashboardQuery();
    const [isLoading, setIsLoading] = useState(false);
    const [ExportComponent, setExportComponent] = useState(null);

    const requestParams = useMemo(() => {
        let privateOrg = organizationSwitch[user?.id] ? organizationSwitch[user?.id].private : null;
        let organizations =
            organizationSwitch[user?.id] && organizationSwitch[user?.id].organizations
                ? Object.keys(organizationSwitch[user?.id].organizations).filter(
                      (key) => organizationSwitch[user?.id].organizations[key] === true
                  )
                : [];
        return {
            ...(organizations.length > 0 ? { organizations: organizations } : {}),
            private: privateOrg
        };
    }, [organizationSwitch, user?.id]);

    const applyFilters = useCallback(async () => {
        if (requestParams.private || requestParams.organizations?.length > 0) {
            setIsLoading(true);
            const result = await dashboardResponse(requestParams);
            if (result?.data?.response) {
                setDashboardData(result.data.response);
            }
            setIsLoading(false);
        }
    }, [dashboardResponse, requestParams]);

    const { data: countriesData } = useGetCountriesQuery({});
    const countries = useMemo(() => {
        if (countriesData?.response?.countries) {
            return countriesData.response.countries.map((country) => ({
                label: country.name,
                value: country.id,
                code: country.code
            }));
        }

        return [];
    }, [countriesData]);

    useEffect(() => {
        setDashboardData([]);
        applyFilters().then();
    }, [applyFilters]);

    const handleExportClick = async () => {
        let ExportDoc = ExportComponent;
        if (!ExportDoc) {
            const { default: LoadedExport } = await import('./OrganizationsDashboardExport');
            setExportComponent(() => LoadedExport);
            ExportDoc = LoadedExport;
        }

        const blob = await pdf(<ExportDoc data={dashboardData} />).toBlob();
        const blobUrl = URL.createObjectURL(blob);

        const dateString = dayjs().format('YYYY-MM-DD');
        const fileName = `organizations-dashboard-${dateString}.pdf`;

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };

    return (
        <AuthLayout title='Organizations Dashboard' backRoute='/dashboard'>
            <>
                {isLoading && <ThreeDotsLoader className='mt-10' />}
                {dashboardData && !isLoading && (
                    <>
                        <div className='flex max-w-3xl w-full mx-auto mt-4 px-8'>
                            <div className='grid sm:grid-cols-2 sm:gap-4 w-full'>
                                <BaseCard className='p-4 py-8 rounded-4xl shadow-mildSubtle text-center'>
                                    <div className='font-bold text-theme1-gold text-[40px] leading-none'>
                                        {formatNumbers(Number(dashboardData.total_organizations))}
                                    </div>
                                    <div className='font-bold text-theme1-title2 mt-1'>Organizations</div>
                                </BaseCard>
                                <BaseCard className='p-4 py-8 rounded-4xl shadow-mildSubtle text-center my-0 sm:my-6'>
                                    <div className='font-bold text-theme1-gold text-[40px] leading-none'>
                                        {formatNumbers(Number(dashboardData.average_profit), '$')}
                                    </div>
                                    <div className='font-bold text-theme1-title2 mt-1'>Average Profit</div>
                                </BaseCard>
                            </div>
                        </div>
                        <div className='flex max-w-3xl w-full mx-auto mt-0 px-8'>
                            <div className='grid sm:grid-cols-2 sm:gap-4 w-full'>
                                <BaseCard className='p-4 py-8 rounded-4xl shadow-mildSubtle text-center'>
                                    <div className='font-bold text-theme1-gold text-[40px] leading-none'>
                                        {formatNumbers(Number(dashboardData.total_active_projects))}
                                    </div>
                                    <div className='font-bold text-theme1-title2 mt-1'>Active Projects</div>
                                </BaseCard>
                                <BaseCard className='p-4 py-8 rounded-4xl shadow-mildSubtle text-center my-0 sm:my-6'>
                                    <div className='font-bold text-theme1-gold text-[40px] leading-none'>
                                        {formatNumbers(Number(dashboardData.total_completed_projects))}
                                    </div>
                                    <div className='font-bold text-theme1-title2 mt-1'>Completed Projects</div>
                                </BaseCard>
                            </div>
                        </div>
                        <div className='flex max-w-3xl w-full mx-auto mt-0 px-8'>
                            <div className='grid sm:grid-cols-2 sm:gap-4 w-full'>
                                <BaseCard className='p-4 py-8 rounded-4xl shadow-mildSubtle text-center'>
                                    <div className='font-bold text-theme1-gold text-[40px] leading-none'>
                                        {formatNumbers(Number(dashboardData.total_costs), '$')}
                                    </div>
                                    <div className='font-bold text-theme1-title2 mt-1'>Expenses</div>
                                </BaseCard>
                                <BaseCard className='p-4 py-8 rounded-4xl shadow-mildSubtle text-center my-0 sm:my-6'>
                                    <div className='font-bold text-theme1-gold text-[40px] leading-none'>
                                        {formatNumbers(Number(dashboardData.total_revenue), '$')}
                                    </div>
                                    <div className='font-bold text-theme1-title2 mt-1'>Revenue</div>
                                </BaseCard>
                            </div>
                        </div>
                        <div className='flex max-w-3xl w-full mx-auto mt-0 px-8'>
                            <div className='grid sm:grid-cols-2 sm:gap-4 w-full'>
                                <BaseCard className='p-4 py-8 rounded-4xl shadow-mildSubtle text-center mb-0 sm:mb-6'>
                                    <div className='font-bold text-theme1-gold text-[40px] leading-none'>
                                        {formatNumbers(Number(dashboardData.total_employees))}
                                    </div>
                                    <div className='font-bold text-theme1-title2 mt-1'>Employees</div>
                                </BaseCard>
                            </div>
                        </div>
                        <div className='flex max-w-3xl w-full mx-auto px-8 mt-6'>
                            <BaseLink className='btn-link-primary' to='/organizations/list'>
                                Organizations List
                            </BaseLink>
                        </div>
                        <div className='flex max-w-3xl w-full mx-auto px-8 mt-4'>
                            <BaseButton className='btn-link-primary' onClick={handleExportClick}>
                                Export full report
                            </BaseButton>
                        </div>
                        {dashboardData.total_organizations > 0 ? (
                            <>
                                {dashboardData.capabilities_distribution?.length > 0 && (
                                    <>
                                        <div className='max-w-3xl w-full mx-auto mt-10 px-8'>
                                            <div className='text-theme1-title2 font-bold text-[22px]'>
                                                Capabilities Overview
                                            </div>

                                            <DashboardMetricsCarousel
                                                items={dashboardData.capabilities_distribution}
                                                total={dashboardData.total_organizations}
                                                displayMode='absolute'
                                            />
                                        </div>
                                    </>
                                )}
                                {dashboardData.needs_distribution?.length > 0 && (
                                    <>
                                        <div className='max-w-3xl w-full mx-auto mt-8 px-8'>
                                            <div className='text-theme1-title2 font-bold text-[22px]'>
                                                Needs Overview
                                            </div>
                                            <DashboardMetricsCarousel
                                                items={dashboardData.needs_distribution}
                                                total={dashboardData.total_organizations}
                                                displayMode='absolute'
                                            />
                                        </div>
                                    </>
                                )}
                                {dashboardData.industries_distribution?.length > 0 && (
                                    <>
                                        <div className='max-w-3xl w-full mx-auto mt-8 px-8'>
                                            <div className='text-theme1-title2 font-bold text-[22px]'>
                                                Industry Distribution
                                            </div>
                                            <DashboardMetricsCarousel
                                                items={dashboardData.industries_distribution || []}
                                                className='mb-6'
                                                total={dashboardData.total_organizations}
                                                clickAction='filter'
                                                filterKey='industry'
                                                displayMode='absolute'
                                                linkBase='/organizations/advanced-search'
                                                transformFilter={(item) =>
                                                    item.id !== undefined ? item.id : item.name
                                                }
                                            />
                                        </div>
                                    </>
                                )}
                                {dashboardData.ownership_distribution?.length > 0 && (
                                    <>
                                        <div className='max-w-3xl w-full mx-auto mt-8 px-8'>
                                            <div className='text-theme1-title2 font-bold text-[22px]'>
                                                Ownership Type
                                            </div>
                                            <DashboardMetricsCarousel
                                                items={dashboardData.ownership_distribution || []}
                                                className='mb-6'
                                                total={dashboardData.total_organizations}
                                                clickAction='filter'
                                                filterKey='ownership'
                                                displayMode='absolute'
                                                linkBase='/organizations/advanced-search'
                                                transformFilter={(item) =>
                                                    item.id !== undefined ? item.id : item.name
                                                }
                                            />
                                        </div>
                                    </>
                                )}
                                {dashboardData.geographical_distribution?.length > 0 && (
                                    <>
                                        <div className='max-w-3xl w-full mx-auto mt-8 px-8'>
                                            <div className='text-theme1-title2 font-bold text-[22px]'>
                                                Geographical Presence
                                            </div>
                                            <DashboardCountryCarousel
                                                items={dashboardData.geographical_distribution || []}
                                                className='mb-6'
                                                countries={countries}
                                                linkBase='/organizations/advanced-search'
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <div className='py-12 flex flex-col justify-center items-center'>
                                    <div className='text-2xl text-theme1-cardStroke font-light max-w-80 text-center'>
                                        You don't have any organizations yet
                                    </div>
                                    <img alt='No organizations' src={circle} className='my-8 w-40 h-40' />
                                    <BaseButton
                                        className='btn-red-outline max-w-96'
                                        onClick={() => navigate(`/organizations/add`)}
                                        textValue='Add new organization'
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
            </>
        </AuthLayout>
    );
};

export default OrganizationsDashboard;
